/**
 * «Команда» без сервера (`VITE_MOCK_API=1`): выдуманные сотрудники, заявки
 * и права в форме ответов `platform-api` и `/api/ops/portal/money`.
 *
 * Роль — та же, что выбрал общий mock портала (`?mock_role=`): всё здесь
 * отвечает только владельцу, остальным — 403, как сервер.
 */

import { ApiError } from '@shell/api';
import type { PortalRequest } from '../../api';
import type {
  AccessRequest,
  MoneyPerson,
  PositionRights,
  RightObject,
  StaffMember,
  TeamUser,
  UserServiceRights,
} from './api';

function isHost(): boolean {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('mock_role');
    const role = fromUrl ?? sessionStorage.getItem('portal-mock-role');
    return !role || role === 'host';
  } catch {
    return true;
  }
}

const SERVICES = ['study', 'ops', 'finance'];

const OBJECTS: RightObject[] = [
  { service: 'ops', code: 'checklists', title: 'Чек-листы', hint: 'проходить чек-листы смены', actions: ['view', 'edit'], is_money: false },
  { service: 'ops', code: 'tasks', title: 'Задачи', hint: 'задачи из проблем смены', actions: ['view', 'edit'], is_money: false },
  { service: 'ops', code: 'receipts', title: 'Накладные', hint: 'сдавать фото и разбирать накладные', actions: ['view', 'edit'], is_money: false },
  { service: 'ops', code: 'attendance', title: 'Табель', hint: 'приход и уход сотрудников', actions: ['view', 'edit'], is_money: false },
  { service: 'ops', code: 'payments', title: 'Оплаты', hint: 'накладные с суммами и долги поставщикам', actions: ['view', 'edit'], is_money: true },
  { service: 'finance', code: 'sales', title: 'Выручка и чеки', hint: 'сводки, выручка, средний чек, чеки', actions: ['view'], is_money: true },
  { service: 'finance', code: 'dishes', title: 'Блюда', hint: 'что продаётся лучше и хуже', actions: ['view'], is_money: true },
  { service: 'finance', code: 'cost', title: 'Себестоимость', hint: 'фудкост, маржа, меню', actions: ['view', 'edit'], is_money: true },
  { service: 'finance', code: 'control', title: 'Контроль', hint: 'сигналы; править — пороги и цели', actions: ['view', 'edit'], is_money: true },
  { service: 'finance', code: 'data', title: 'Данные', hint: 'откуда цифры и насколько свежие', actions: ['view'], is_money: true },
  { service: 'finance', code: 'assistant', title: 'Вопросы словами', hint: 'в пределах открытых разделов', actions: ['view'], is_money: true },
  { service: 'study', code: 'catalog', title: 'Справочник блюд', hint: 'состав и аллергены', actions: ['view', 'edit'], is_money: false },
  { service: 'study', code: 'courses', title: 'Курсы', hint: 'курсы и тесты', actions: ['view', 'edit'], is_money: false },
  { service: 'study', code: 'mentor', title: 'Наставник', hint: 'личные показатели и цели', actions: ['view'], is_money: false },
];

const positions: PositionRights[] = [
  { id: 1, name: 'Собственник', is_owner: true, rights: {} },
  { id: 2, name: 'Управляющий', is_owner: false, is_waiter: false, rights: { ops: { configured: true, granted: ['checklists:view', 'checklists:edit', 'tasks:view', 'tasks:edit', 'receipts:view', 'receipts:edit', 'attendance:view', 'attendance:edit'] }, study: { configured: true, granted: ['catalog:view', 'catalog:edit', 'courses:view', 'courses:edit'] } } },
  { id: 3, name: 'Шеф-повар', is_owner: false, rights: { ops: { configured: true, granted: ['checklists:view', 'checklists:edit', 'tasks:view', 'receipts:view', 'receipts:edit', 'attendance:view'] }, study: { configured: false, granted: ['catalog:view'] } } },
  { id: 4, name: 'Повар', is_owner: false, rights: { ops: { configured: true, granted: ['checklists:view', 'tasks:view'] }, study: { configured: false, granted: ['catalog:view', 'courses:view'] } } },
  // Единственная отмеченная официантской (D247): по ней считается разрез
  // «по официантам» и витрина наставника.
  { id: 5, name: 'Официант', is_owner: false, is_waiter: true, rights: { ops: { configured: false, granted: ['checklists:view'] }, study: { configured: true, granted: ['catalog:view', 'courses:view', 'mentor:view'] } } },
];

/** Сотрудники без Telegram — штат, заведённый для табеля. */
const staff: StaffMember[] = [
  { employee_id: 101, full_name: 'Иванова Алина', position_id: 3, position_name: 'Повар', employee_status: 'active' },
  { employee_id: 102, full_name: 'Петров Олег', position_id: 5, position_name: 'Официант', employee_status: 'active' },
];

const users: TeamUser[] = [
  { user_id: 1, full_name: 'Пётр Соколов', position_id: 1, position_name: 'Собственник', employee_status: 'active', is_host: true, access: { study: 'host', ops: 'host', finance: 'host' } },
  { user_id: 2, full_name: 'Анна Ковалёва', position_id: 2, position_name: 'Управляющий', employee_status: 'active', is_host: false, access: { ops: 'admin', study: 'admin', finance: 'user' } },
  { user_id: 3, full_name: 'Олег Васильев', position_id: 3, position_name: 'Шеф-повар', employee_status: 'active', is_host: false, access: { ops: 'user', study: 'user' } },
  { user_id: 4, full_name: 'Иван Морозов', position_id: 4, position_name: 'Повар', employee_status: 'active', is_host: false, access: { ops: 'user', study: 'user' } },
  { user_id: 5, full_name: 'Мария Лебедева', position_id: 5, position_name: 'Официант', employee_status: 'active', is_host: false, access: { study: 'user' } },
  { user_id: 6, full_name: 'Ирина Смирнова', position_id: null, position_name: null, employee_status: 'active', is_host: false, access: { ops: 'user' } },
  { user_id: 7, full_name: 'Денис Павлов', position_id: 5, position_name: 'Официант', employee_status: 'fired', is_host: false, access: {} },
];

const money: Record<number, boolean> = { 2: true, 3: false, 4: false, 6: false };
const personal: Record<number, Record<string, Record<string, boolean>>> = {
  3: { ops: { 'payments:view': false } },
};

let requests: AccessRequest[] = [
  { id: 41, service: 'study', services: ['study', 'ops'], applicant_name: 'Ольга Никитина', applicant_note: null, requested_at: new Date(Date.now() - 3 * 3_600_000).toISOString(), candidates: [{ employee_id: 79, full_name: 'Лосев Ярослав', position_name: 'Официант', match: 'none' }] },
  { id: 42, service: 'ops', services: ['ops'], applicant_name: 'Сергей Орлов', applicant_note: 'Новый бармен, с понедельника', requested_at: new Date(Date.now() - 26 * 3_600_000).toISOString(),
    candidates: [
      { employee_id: 77, full_name: 'Орлов Сергей', position_name: 'Бармен', match: 'exact' },
      { employee_id: 78, full_name: 'Орлова Мария', position_name: 'Официант', match: 'partial' },
      { employee_id: 79, full_name: 'Лосев Ярослав', position_name: 'Официант', match: 'none' },
    ] },
];

function rightsOf(user: TeamUser): Record<string, UserServiceRights> {
  const result: Record<string, UserServiceRights> = {};
  const position = positions.find((item) => item.id === user.position_id);
  for (const service of SERVICES) {
    const level = user.access[service] ?? null;
    const granted: Record<string, string> = {};
    const removed: string[] = [];
    let base = 'position';
    if (user.is_host) {
      base = 'host';
      for (const item of OBJECTS.filter((o) => o.service === service)) {
        for (const action of item.actions) granted[`${item.code}:${action}`] = 'host';
      }
    } else {
      const fromPosition = new Set(position?.rights[service]?.granted ?? []);
      if (level === 'admin') {
        base = 'admin';
        for (const item of OBJECTS.filter((o) => o.service === service && !o.is_money)) {
          for (const action of item.actions) fromPosition.add(`${item.code}:${action}`);
        }
      }
      // Вход в финансы открывает все их разделы (D230).
      if (service === 'finance' && level) {
        base = 'access';
        for (const item of OBJECTS.filter((o) => o.service === service)) {
          for (const action of item.actions) fromPosition.add(`${item.code}:${action}`);
        }
      }
      if (service === 'ops' && money[user.user_id]) {
        fromPosition.add('payments:view');
        fromPosition.add('payments:edit');
      }
      const source = base === 'access' ? 'access' : level === 'admin' ? 'admin' : 'position';
      for (const key of fromPosition) granted[key] = source;
      if (service === 'ops' && money[user.user_id]) {
        granted['payments:view'] = 'added';
        granted['payments:edit'] = 'added';
      }
      for (const [key, on] of Object.entries(personal[user.user_id]?.[service] ?? {})) {
        if (on) granted[key] = 'added';
        else {
          delete granted[key];
          removed.push(key);
        }
      }
    }
    result[service] = { access_level: user.is_host ? 'host' : level, base, granted, removed };
  }
  return result;
}

function moneyPeople(): MoneyPerson[] {
  return users
    .filter((user) => !user.is_host && user.access.ops && user.employee_status !== 'fired')
    .map((user) => ({
      user_id: user.user_id,
      employee_id: 100 + user.user_id,
      full_name: user.full_name ?? '',
      level: user.access.ops,
      has_finance: Boolean(money[user.user_id]),
    }));
}

/**
 * Коды кассы без сервера — форма ответа и три состояния строки.
 *
 * `code` и `till_code` намеренно разные: в чеке лежит ключ справочника
 * («1000037»), а официанта в ресторане знают по номеру станции («17»).
 * Заглушка, в которой они совпадали бы, прятала бы ровно ту ошибку,
 * из-за которой 29.09.2026 связывание не работало бы вовсе.
 */
const waiters = [
  {
    code: '1000012',
    till_code: '12',
    till_name: 'Алина',
    employee_id: 3,
    employee_name: 'Иван Морозов',
    is_active: true,
    candidates: [] as {
      employee_id: number;
      full_name: string;
      position_name: string | null;
      match: string;
    }[],
  },
  {
    code: '1000018',
    till_code: '18',
    till_name: 'Катя',
    employee_id: null as number | null,
    employee_name: null as string | null,
    is_active: true,
    candidates: [
      { employee_id: 7, full_name: 'Хаитбоева Кундузхон (Катя)', position_name: 'Официант', match: 'exact' },
      { employee_id: 9, full_name: 'Екатерина Смирнова', position_name: 'Бармен', match: 'partial' },
    ],
  },
  {
    code: '1000004',
    till_code: '4',
    till_name: 'Администратор',
    employee_id: null as number | null,
    employee_name: null as string | null,
    is_active: false,
    candidates: [] as {
      employee_id: number;
      full_name: string;
      position_name: string | null;
      match: string;
    }[],
  },
];

export async function mock(path: string, options: PortalRequest): Promise<unknown | undefined> {
  const [pathname] = path.split('?');
  const method = options.method ?? 'GET';
  const body = (options.body ?? {}) as Record<string, unknown>;
  const mine =
    pathname === '/api/platform/users' ||
    pathname === '/api/platform/rights' ||
    pathname === '/api/platform/invite' ||
    pathname === '/api/platform/positions' ||
    pathname === '/api/platform/employees' ||
    pathname === '/api/platform/waiters' ||
    /^\/api\/platform\/waiters\//.test(pathname) ||
    /^\/api\/platform\/(users|positions|access-requests|employees)\//.test(pathname) ||
    pathname.startsWith('/api/ops/portal/money');
  if (!mine) return undefined;
  if (!isHost()) throw new ApiError(403, 'forbidden');

  // Официанты кассы: один закреплён, один с точным совпадением,
  // один выключен в кассе — три состояния, которые видны на экране.
  // Заведение сотрудника: тёзка отклоняется кодом 409, пока
  // человек не подтвердил, что это другой человек.
  if (pathname === '/api/platform/employees' && method === 'POST') {
    const fullName = String(body.full_name ?? '').trim();
    if (fullName.length < 2) throw new ApiError(400, 'bad', 'Напишите имя и фамилию.');
    const words = (value: string) =>
      value.toLowerCase().replace(/ё/g, 'е').split(/[^а-яa-z0-9-]+/).filter(Boolean).sort().join(' ');
    const twin = staff.find((member) => words(member.full_name) === words(fullName));
    if (twin && !body.allow_duplicate) {
      throw new ApiError(409, 'twin', `В справочнике уже есть «${twin.full_name}». Если это другой человек — подтвердите заведение ещё раз.`);
    }
    const employeeId = 900 + staff.length;
    const position = positions.find((item) => item.id === body.position_id);
    staff.push({
      employee_id: employeeId,
      full_name: fullName,
      position_id: position?.id ?? null,
      position_name: position?.name ?? null,
    } as (typeof staff)[number]);
    return { employee_id: employeeId, full_name: fullName };
  }

  if (pathname === '/api/platform/waiters' && method === 'GET') {
    return structuredClone({ waiters: waiters });
  }
  if (/^\/api\/platform\/waiters\//.test(pathname) && method === 'PUT') {
    const code = decodeURIComponent(pathname.split('/').pop() ?? '');
    const employeeId = (body.employee_id ?? null) as number | null;
    const row = waiters.find((item) => item.code === code);
    if (row) {
      row.employee_id = employeeId;
      row.employee_name =
        employeeId === null
          ? null
          : (row.candidates.find((c) => c.employee_id === employeeId)?.full_name ?? null);
      row.candidates = employeeId === null ? row.candidates : [];
    }
    return { code };
  }

  if (pathname === '/api/platform/users') {
    return structuredClone({
      me: { user_id: 1 },
      services: SERVICES,
      users,
      requests,
      employees_without_account: staff.length,
      staff,
      positions: positions.map(({ id, name, is_owner }) => ({ id, name, is_owner })),
    });
  }
  if (pathname === '/api/platform/rights') {
    const withPeople = positions.map((position) => ({
      ...position,
      people: [
        ...users
          .filter((user) => user.position_id === position.id && user.employee_status !== 'fired')
          .map((user) => ({ employee_id: user.user_id + 1000, user_id: user.user_id, full_name: user.full_name ?? '' })),
        ...staff
          .filter((member) => member.position_id === position.id)
          .map((member) => ({ employee_id: member.employee_id, user_id: null, full_name: member.full_name })),
      ],
    }));
    return structuredClone({ services: SERVICES, objects: OBJECTS, positions: withPeople });
  }
  const staffMatch = /^\/api\/platform\/employees\/(\d+)\/(position|fire)$/.exec(pathname);
  if (staffMatch) {
    const index = staff.findIndex((member) => member.employee_id === Number(staffMatch[1]));
    if (index < 0) throw new ApiError(404, 'not_found');
    if (staffMatch[2] === 'fire') {
      staff.splice(index, 1);
      return { status: 'fired' };
    }
    const position = positions.find((item) => item.id === body.position_id) ?? null;
    staff[index].position_id = position?.id ?? null;
    staff[index].position_name = position?.name ?? null;
    return { position_id: staff[index].position_id };
  }
  if (pathname === '/api/platform/invite') {
    return {
      how: 'Новый сотрудник открывает любого из ботов, нажимает «Запросить доступ» и заполняет короткую анкету. Заявка у человека одна на все боты и появится здесь — вы назначите должность, уровень и агенты.',
    };
  }
  if (pathname === '/api/platform/positions' && method === 'POST') {
    const name = String(body.name ?? '').trim();
    if (!name || positions.some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      throw new ApiError(400, 'bad_request');
    }
    const source = positions.find((item) => item.id === body.copy_from);
    const id = Math.max(...positions.map((item) => item.id)) + 1;
    const rights = structuredClone(source?.rights ?? {});
    positions.push({ id, name, is_owner: false, rights });
    const copied = Object.values(rights).reduce((sum, item) => sum + item.granted.length, 0);
    return { position_id: id, name, rights_copied: copied };
  }

  let match = /^\/api\/platform\/positions\/(\d+)$/.exec(pathname);
  if (match) {
    const index = positions.findIndex((item) => item.id === Number(match![1]));
    if (index < 0) throw new ApiError(404, 'not_found');
    const position = positions[index];
    if (method === 'PUT') {
      // Отметка «официант» приходит одна, без названия (D247): её ставят
      // касанием, не открывая правку.
      if (body.is_waiter !== undefined) {
        position.is_waiter = Boolean(body.is_waiter);
        return { position_id: position.id, is_waiter: position.is_waiter };
      }
      position.name = String(body.name ?? '').trim() || position.name;
      return { position_id: position.id, name: position.name };
    }
    if (method === 'DELETE') {
      if (position.is_owner) throw new ApiError(400, 'bad_request', '«Собственник» удалить нельзя.');
      const people = users.filter((user) => user.position_id === position.id).length;
      if (people) {
        throw new ApiError(400, 'bad_request', `На должности ${people} чел. Сначала назначьте им другую.`);
      }
      positions.splice(index, 1);
      return { position_id: position.id, result: 'deleted' };
    }
  }

  match = /^\/api\/platform\/positions\/(\d+)\/rights$/.exec(pathname);
  if (match) {
    const position = positions.find((item) => item.id === Number(match![1]));
    if (!position) throw new ApiError(404, 'not_found');
    const object = OBJECTS.find((item) => item.service === body.service && item.code === body.object);
    if (position.is_owner || object?.is_money) throw new ApiError(403, 'forbidden');
    const service = String(body.service);
    const current = position.rights[service] ?? { configured: false, granted: [] };
    const key = `${body.object}:${body.action}`;
    const set = new Set(current.granted);
    if (body.granted) set.add(key);
    else set.delete(key);
    position.rights[service] = { configured: true, granted: [...set] };
    return { position_id: position.id, change: 'set' };
  }

  match = /^\/api\/platform\/users\/(\d+)\/(access|position|rights|fire|copy)(?:\/(\w+))?$/.exec(pathname);
  if (match) {
    const user = users.find((item) => item.user_id === Number(match![1]));
    if (!user) throw new ApiError(404, 'not_found');
    const what = match[2];
    if (what === 'copy' && method === 'PUT') {
      user.copy_granted = Boolean(body.allowed);
      user.can_copy = user.copy_granted;
      return { user_id: user.user_id, copy_granted: user.copy_granted };
    }
    if (what === 'fire' && method === 'POST') {
      const revoked = Object.keys(user.access);
      user.access = {};
      user.employee_status = 'fired';
      return { user_id: user.user_id, status: 'fired', revoked, sessions: 1 };
    }
    if (what === 'access' && method === 'PUT') {
      user.access[String(body.service)] = String(body.access_level);
      return { change: 'granted' };
    }
    if (what === 'access' && method === 'DELETE' && match[3]) {
      delete user.access[match[3]];
      return { change: 'revoked' };
    }
    if (what === 'position') {
      const position = positions.find((item) => item.id === body.position_id) ?? null;
      user.position_id = position?.id ?? null;
      user.position_name = position?.name ?? null;
      const opened: string[] = [];
      if (position?.is_owner) {
        for (const service of SERVICES) {
          if (!user.access[service]) {
            user.access[service] = 'admin';
            opened.push(service);
          }
        }
      }
      return { user_id: user.user_id, position_id: user.position_id, opened };
    }
    if (what === 'rights' && method === 'GET') {
      return { user_id: user.user_id, services: rightsOf(user) };
    }
    if (what === 'rights' && method === 'PUT') {
      const service = String(body.service);
      const key = `${body.object}:${body.action}`;
      const table = (personal[user.user_id] ??= {});
      const serviceTable = (table[service] ??= {});
      if (body.granted === null) delete serviceTable[key];
      else serviceTable[key] = Boolean(body.granted);
      return { change: 'set' };
    }
    if (what === 'rights' && method === 'DELETE') {
      const service = new URLSearchParams(path.split('?')[1] ?? '').get('service');
      if (service) delete personal[user.user_id]?.[service];
      else delete personal[user.user_id];
      return { removed: 1 };
    }
  }

  match = /^\/api\/platform\/access-requests\/(\d+)\/(approve|reject)$/.exec(pathname);
  if (match) {
    const request = requests.find((item) => item.id === Number(match![1]));
    if (!request) throw new ApiError(400, 'bad_request');
    requests = requests.filter((item) => item !== request);
    if (match[2] === 'reject') return { status: 'rejected' };
    const position = positions.find((item) => item.id === body.position_id) ?? null;
    const id = Math.max(...users.map((item) => item.user_id)) + 1;
    const access: Record<string, string> = {};
    for (const service of (body.services as string[]) ?? []) access[service] = String(body.access_level);
    users.push({
      user_id: id,
      full_name: request.applicant_name,
      position_id: position?.id ?? null,
      position_name: position?.name ?? null,
      employee_status: 'active',
      is_host: false,
      access,
    });
    return { user_id: id, status: 'approved' };
  }

  if (pathname === '/api/ops/portal/money') return { people: moneyPeople() };
  match = /^\/api\/ops\/portal\/money\/(\d+)$/.exec(pathname);
  if (match) {
    const person = moneyPeople().find((item) => item.employee_id === Number(match![1]));
    if (!person) {
      throw new ApiError(404, 'not found', 'Этого сотрудника в операционном агенте нет: сначала выдайте ему вход в агент.');
    }
    money[person.user_id] = Boolean(body.on);
    return {
      message: `${person.full_name}: доступ к деньгам ${body.on ? 'выдан' : 'снят'}.`,
      person: { ...person, has_finance: Boolean(body.on) },
    };
  }
  return undefined;
}
